/**
 * /zh-tw/video-to-text — 影片轉文字 reframe bridge (Traditional Chinese).
 *
 * Targets 影片轉文字 (2.3k) / 影片轉文字免費 (500) / 影片逐字稿 (600).
 * Links to the /zh-tw/voice-to-text pillar + verbatim-transcript.
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwVideoToText() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/video-to-text"
      hreflangLocales={['zhtw']}
      documentTitle="影片轉文字｜免費把 YouTube／會議影片變逐字稿 · Draftlize"
      documentDescription="教你免費把影片、YouTube 影片轉成文字。會議影片別只停在「文字」，把裡面的決策抽出來才有價值。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 影片轉文字</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            影片轉文字，<br />
            <em>留下真正重要的部分。</em>
          </h1>
          <p className="lede">
            把會議錄影或 YouTube 丟進去，幾分鐘就有逐字稿。方便，但影片轉出來的文字和語音一樣，只是<em>素材</em>。尤其會議影片，你真正需要的不是全部發言，而是裡面下的<em>決策</em>和理由。這頁教你免費把影片轉文字，以及下一步——留下的是決策，不只是文字。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">免費把影片轉文字。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>準備影片</strong>——會議錄影、螢幕錄影，或 YouTube 連結。</li>
            <li><strong>轉文字</strong>——支援影片的 AI 工具把聲音軌轉成文字，多半有免費額度。</li>
            <li><strong>校對</strong>——把講者、專有名詞順過再匯出。</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            YouTube 影片同樣做法。字幕也能用，但字幕是「拿來讀的文字」。會議影片還有下一步——先看 <a href="/zh-tw/voice-to-text" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>語音轉文字</a> 的思路。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">字幕 vs 決策</span>
            <h2 className="serif">別停在把影片變文字，<em>把決策留下來。</em></h2>
          </div>
          <p>影片轉文字工具和 Draftlize 做的事不同。一個把影片變文字，一個讓文字裡的決策一直有效。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>影片轉文字</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>產出</td><td className="partial">字幕・逐字稿</td><td className="ours yes">帶理由的決策卡片</td></tr>
            <tr><td>會議影片的價值</td><td className="no">埋在全部發言裡</td><td className="ours yes">濃縮成幾個決策</td></tr>
            <tr><td>之後回看</td><td className="no">太長沒人看</td><td className="ours yes">用 ID 引用・是否最新一目了然</td></tr>
            <tr><td>AI agent</td><td className="partial">只讀字幕</td><td className="ours yes">用 MCP 讀寫決策</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          就算把一小時的會議影片全部轉成文字，裡面哪個決策現在還算數，文字也不會告訴你。
        </blockquote>
        <cite>影片轉文字交給工具。讓決策一直有效的是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '影片轉文字工具推薦？', a: '用支援影片的 AI 轉文字工具，就能自動把聲音軌變文字。Draftlize 不做轉文字——它在文字出來後，把裡面的決策變成能保持最新的卡片。' },
            { q: 'YouTube 怎麼轉文字？', a: '把 YouTube 連結交給支援的工具，或匯出自動字幕。若是會議或訪談影片，再加上「留下決策」這一步，價值就不一樣。' },
            { q: '支援多國語言嗎？', a: '多數工具支援多語。但比語言更重要的是轉完之後留下哪些決策，這正是 Draftlize 負責的。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">從會議影片，只把決策變成活的卡片<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            影片轉文字交給工具，把摘要貼進 Draftlize。會議影片裡的決策，就變成互相連結、保持最新、AI agent 也讀得到的卡片。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
